/**
 * BulkInviteModal - Two-step CSV bulk invite modal
 * Step 1: Upload CSV  →  Step 2: Preview & confirm
 */

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, Download, AlertTriangle } from 'lucide-react';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import invitationService from '../../services/api/invitationService';
import type { BulkValidateResponse, BulkValidateRow } from '../../services/api/invitationService';
import {
  DropZone,
  DropLabel,
  DropSubLabel,
  FileNameChip,
  SummaryBar,
  ValidCount,
  InvalidCount,
  TotalCount,
  TableWrapper,
  Table,
  Th,
  Td,
  Tr,
  StatusIcon,
  ErrorBanner,
  ResultBanner,
  ResultIcon,
  ResultTitle,
  ResultSubtext,
  ModalFooter,
  FooterRight,
  LoadingState,
} from '../shared/BulkUploadShared';
import { downloadErrorsCsv, downloadTemplateCsv } from '../shared/bulkUploadUtils';

// ============================================================================
// Helpers
// ============================================================================

const handleDownloadErrors = (rows: BulkValidateRow[]) => {
  downloadErrorsCsv(
    rows.map((r) => ({
      row: r.row,
      email: r.email,
      name: r.name,
      role: r.role,
      reason: r.reason,
      valid: r.valid,
    })),
    ['email', 'name', 'role'],
    'invite-errors.csv',
  );
};

const SAMPLE_HEADERS = ['email', 'name', 'role'];

const handleDownloadSample = () => {
  downloadTemplateCsv(
    SAMPLE_HEADERS,
    [
      {
        email: 'alex.rivera@example.com',
        name: 'Alex Rivera',
        role: 'RBT',
      },
      {
        email: 'morgan.lee@example.com',
        name: 'Morgan Lee',
        role: 'BCBA',
      },
    ],
    'team-bulk-invite-template.csv',
  );
};

const UploadActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

// ============================================================================
// Component
// ============================================================================

type Step = 'upload' | 'preview' | 'result';

interface BulkInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BulkInviteModal = ({ isOpen, onClose }: BulkInviteModalProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validateResult, setValidateResult] = useState<BulkValidateResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [executeResult, setExecuteResult] = useState<{ created: number; skipped: number } | null>(
    null,
  );

  const validateMutation = useMutation({
    mutationFn: (file: File) => invitationService.bulkValidate(file),
    onSuccess: (result) => {
      setValidateResult(result);
      setStep('preview');
    },
    onError: () => {
      setUploadError('Failed to validate the CSV file. Please check the format and try again.');
    },
  });

  const executeMutation = useMutation({
    mutationFn: () => {
      const validRows = (validateResult?.rows ?? [])
        .filter((r) => r.valid)
        .map((r) => ({ email: r.email, name: r.name, roleId: r.roleId }));
      return invitationService.bulkExecute(validRows);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setExecuteResult({ created: result.created, skipped: result.skipped });
      setStep('result');
    },
    onError: () => {
      setUploadError('Failed to send invitations. Please try again.');
    },
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setUploadError('Please upload a .csv file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 5 MB. Please upload a smaller file.');
      return;
    }
    setUploadError(null);
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setValidateResult(null);
    setUploadError(null);
    setExecuteResult(null);
    onClose();
  };

  const rows = validateResult?.rows ?? [];
  const validCount = rows.filter((r) => r.valid).length;
  const invalidCount = rows.filter((r) => !r.valid).length;

  const getTitle = () => {
    if (step === 'upload') return 'Bulk Invite — Upload CSV';
    if (step === 'preview') return 'Bulk Invite — Preview';
    return 'Bulk Invite — Complete';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} size="lg">
      {step === 'upload' && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload CSV file"
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <UploadCloud />
            <DropLabel>Drop your CSV here or click to browse</DropLabel>
            <DropSubLabel>Accepted format: .csv — columns: email, name, role</DropSubLabel>
            {selectedFile && <FileNameChip>{selectedFile.name}</FileNameChip>}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />

          <UploadActions>
            <Button variant="ghost" leftIcon={<Download />} onClick={handleDownloadSample}>
              Download Sample CSV
            </Button>
          </UploadActions>

          {validateMutation.isPending && <LoadingState>Validating CSV...</LoadingState>}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => selectedFile && validateMutation.mutate(selectedFile)}
                disabled={!selectedFile || validateMutation.isPending}
                isLoading={validateMutation.isPending}
              >
                Upload & Validate
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {step === 'preview' && validateResult && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <SummaryBar>
            <ValidCount>
              <CheckCircle />
              {validCount} valid
            </ValidCount>
            <InvalidCount>
              <XCircle />
              {invalidCount} invalid
            </InvalidCount>
            <TotalCount>out of {rows.length} total</TotalCount>
          </SummaryBar>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Row</Th>
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.row} $invalid={!row.valid}>
                    <Td $invalid={!row.valid}>{row.row}</Td>
                    <Td $invalid={!row.valid}>{row.email}</Td>
                    <Td $invalid={!row.valid}>{row.name ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.role ?? '—'}</Td>
                    <Td>
                      <StatusIcon $valid={row.valid}>
                        {row.valid ? <CheckCircle /> : <XCircle />}
                      </StatusIcon>
                    </Td>
                    <Td $invalid={!row.valid}>{row.reason ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <FooterRight>
              {invalidCount > 0 && (
                <Button
                  variant="ghost"
                  leftIcon={<Download />}
                  onClick={() => handleDownloadErrors(rows)}
                >
                  Download Errors
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => executeMutation.mutate()}
                disabled={validCount === 0 || executeMutation.isPending}
                isLoading={executeMutation.isPending}
              >
                Send {validCount} Invitation{validCount !== 1 ? 's' : ''}
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {step === 'result' && executeResult && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>Invitations sent!</ResultTitle>
            <ResultSubtext>
              {executeResult.created} invitation{executeResult.created !== 1 ? 's' : ''} created
              {executeResult.skipped > 0 && `, ${executeResult.skipped} skipped`}.
            </ResultSubtext>
          </ResultBanner>

          <ModalFooter>
            <div />
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default BulkInviteModal;
